/**
 * Utilities for managing localStorage caches
 */

/**
 * Clean old pattern caches to prevent localStorage bloat
 * Removes caches older than 7 days
 */
export function cleanOldPatternCaches(): void {
  if (typeof window === "undefined") return

  try {
    const keys = Object.keys(localStorage)
    const patternKeys = keys.filter((k) => k.startsWith("pattern-cache-"))

    let removedCount = 0
    const maxAge = 7 * 24 * 60 * 60 * 1000 // 7 days in milliseconds

    patternKeys.forEach((key) => {
      try {
        const cached = localStorage.getItem(key)
        if (!cached) return

        const cacheData = JSON.parse(cached)
        const age = Date.now() - cacheData.timestamp

        // Remove if older than 7 days
        if (age > maxAge) {
          localStorage.removeItem(key)
          removedCount++
        }
      } catch (parseError) {
        // Invalid cache entry, remove it
        localStorage.removeItem(key)
        removedCount++
      }
    })

    if (removedCount > 0) {
      console.log(`Cleaned ${removedCount} old pattern cache(s)`)
    }
  } catch (error) {
    console.error("Error cleaning pattern caches:", error)
  }
}

/**
 * Get localStorage usage statistics
 * Useful for debugging cache bloat
 */
export function getCacheStats(): {
  patternCaches: number
  totalSize: number
  oldestCache: number | null
} {
  if (typeof window === "undefined") {
    return { patternCaches: 0, totalSize: 0, oldestCache: null }
  }

  try {
    const keys = Object.keys(localStorage)
    const patternKeys = keys.filter((k) => k.startsWith("pattern-cache-"))

    let totalSize = 0
    let oldestTimestamp: number | null = null

    patternKeys.forEach((key) => {
      try {
        const cached = localStorage.getItem(key)
        if (cached) {
          totalSize += cached.length
          const cacheData = JSON.parse(cached)
          if (!oldestTimestamp || cacheData.timestamp < oldestTimestamp) {
            oldestTimestamp = cacheData.timestamp
          }
        }
      } catch {
        // Ignore invalid entries
      }
    })

    return {
      patternCaches: patternKeys.length,
      totalSize,
      oldestCache: oldestTimestamp,
    }
  } catch (error) {
    console.error("Error getting cache stats:", error)
    return { patternCaches: 0, totalSize: 0, oldestCache: null }
  }
}

/**
 * Clear all pattern caches
 * Use with caution - this will force regeneration of all AI patterns
 */
export function clearAllPatternCaches(): void {
  if (typeof window === "undefined") return

  try {
    const keys = Object.keys(localStorage)
    const patternKeys = keys.filter((k) => k.startsWith("pattern-cache-"))

    patternKeys.forEach((key) => {
      localStorage.removeItem(key)
    })

    console.log(`Cleared ${patternKeys.length} pattern cache(s)`)
  } catch (error) {
    console.error("Error clearing pattern caches:", error)
  }
}
